import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Globe, TrendingUp, Users, Eye, MousePointerClick, AlertCircle } from "lucide-react";

interface WebsiteAnalytics {
  pageviews: number;
  visitors: number;
  sessions: number;
  bounce_rate: number | null;
  avg_session_duration: number | null;
  top_pages: { path: string; views: number }[];
  period: string;
  provider: string;
}

const PROVIDER_LABELS: Record<string, string> = {
  ga4: 'Google Analytics 4',
  plausible: 'Plausible',
  matomo: 'Matomo',
  cloudflare: 'Cloudflare',
};

const PERIOD_OPTIONS = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
];

function fmtNum(n: number) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

function fmtDuration(secs: number) {
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}m ${s}s`;
}

export function WebsiteAnalyticsWidget({ onNavigate }: { onNavigate?: (view: string) => void }) {
  const [period, setPeriod] = useState('30d');

  // Check if org has analytics configured before hitting the edge function
  const { data: hasConfig, isLoading: configLoading } = useQuery({
    queryKey: ['org_analytics_config_exists'],
    queryFn: async () => {
      const { data } = await (supabase
        .from('org_analytics_config' as any)
        .select('id, is_active')
        .eq('is_active', true)
        .maybeSingle() as any);
      return !!data;
    },
    staleTime: 60 * 1000,
  });

  const { data, isLoading, error } = useQuery<WebsiteAnalytics>({
    queryKey: ['website_analytics', period],
    enabled: hasConfig === true,
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');
      const res = await supabase.functions.invoke('fetch-website-analytics', {
        body: { period },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (res.error) throw new Error(res.error.message);
      if (res.data?.error) throw new Error(res.data.error);
      return res.data as WebsiteAnalytics;
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Website Analytics</h3>
          {data?.provider && (
            <Badge variant="secondary" className="text-xs">{PROVIDER_LABELS[data.provider] ?? data.provider}</Badge>
          )}
        </div>
        <div className="flex gap-1">
          {PERIOD_OPTIONS.map(o => (
            <button
              key={o.value}
              onClick={() => setPeriod(o.value)}
              className={`text-xs px-2 py-0.5 rounded transition-colors ${
                period === o.value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {(configLoading || (hasConfig && isLoading)) && (
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="h-16 bg-muted/40 rounded-lg animate-pulse" />
          ))}
        </div>
      )}

      {!configLoading && !hasConfig && (
        <div className="flex flex-col items-center justify-center py-8 text-center gap-3">
          <AlertCircle className="w-8 h-8 text-muted-foreground/50" />
          <div>
            <p className="text-sm font-medium">No analytics provider connected</p>
            <p className="text-xs text-muted-foreground mt-1">
              Connect Google Analytics, Plausible, Matomo, or Cloudflare in{' '}
              <button
                className="underline hover:text-foreground"
                onClick={() => onNavigate?.('org-settings')}
              >
                Org Settings
              </button>
              .
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 py-4 text-sm text-destructive">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{(error as Error).message}</span>
        </div>
      )}

      {data && (
        <div className="space-y-4">
          {/* Metric tiles */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MetricTile icon={Eye} label="Pageviews" value={fmtNum(data.pageviews)} />
            <MetricTile icon={Users} label="Visitors" value={fmtNum(data.visitors)} />
            <MetricTile icon={TrendingUp} label="Sessions" value={fmtNum(data.sessions)} />
            <MetricTile
              icon={MousePointerClick}
              label="Bounce Rate"
              value={data.bounce_rate != null ? `${data.bounce_rate.toFixed(1)}%` : '—'}
            />
          </div>

          {data.avg_session_duration != null && (
            <p className="text-xs text-muted-foreground">
              Avg. session duration: <strong>{fmtDuration(data.avg_session_duration)}</strong>
            </p>
          )}

          {/* Top pages */}
          {data.top_pages.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Top Pages</p>
              <div className="space-y-1.5">
                {data.top_pages.slice(0, 5).map((p, i) => {
                  const max = data.top_pages[0]?.views ?? 1;
                  return (
                    <div key={i} className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-xs truncate text-foreground/80">{p.path}</span>
                          <span className="text-xs text-muted-foreground ml-2 shrink-0">{fmtNum(p.views)}</span>
                        </div>
                        <div className="h-1 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-[#006B3C] rounded-full"
                            style={{ width: `${(p.views / max) * 100}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function MetricTile({ icon: Icon, label, value }: { icon: any; label: string; value: string }) {
  return (
    <div className="bg-muted/30 rounded-lg p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-lg font-semibold">{value}</p>
    </div>
  );
}
